import React, { useState } from "react";
import { Check, SkipForward, X, Loader2, Camera } from "lucide-react";
import PoseCamera from "@/components/progress/PoseCamera";

const STEPS = [
  { pose: "front", label: "Front on", hint: "Face the camera, arms relaxed by your sides, feet hip-width." },
  { pose: "side", label: "Side on", hint: "Turn 90°, same spot, arms hanging naturally — don't suck in." },
  { pose: "back", label: "Back on", hint: "Turn away from the camera, heels on the same mark." },
];

// Full set in one flow: front → side → back, each with its own ghost guide.
// Uploads as it goes so a dropped connection never loses earlier shots.
export default function PoseSession({ ghostFor, onUpload, onDone }) {
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);   // poses captured this session

  const step = STEPS[idx];
  const finished = idx >= STEPS.length;

  const advance = () => setIdx((i) => i + 1);

  const capture = async (file) => {
    setBusy(true);
    const ok = await onUpload(file, step.pose);
    setBusy(false);
    if (ok) setDone((d) => [...d, step.pose]);
    advance();
  };

  if (finished) {
    return (
      <div className="border border-[#E2E30A]/40 bg-[#E2E30A]/[0.05] p-4 space-y-3" data-testid="pose-session-summary">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold">Set complete</p>
        <p className="text-sm text-zinc-100">
          {done.length === 3
            ? "All three angles captured — that's a full set for this date."
            : done.length === 0
              ? "No photos captured this time."
              : `Captured ${done.join(", ")}. ${STEPS.filter((s) => !done.includes(s.pose)).map((s) => s.pose).join(" and ")} still missing for a full set.`}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="pose-session-restart"
            onClick={() => { setIdx(0); setDone([]); }}
            className="h-11 border border-white/15 hover:border-white/40 text-white text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
            <Camera className="h-4 w-4" /> Shoot again
          </button>
          <button type="button" data-testid="pose-session-close" onClick={onDone}
            className="h-11 bg-[#E2E30A] hover:bg-white text-black text-[11px] uppercase font-bold tracking-wider inline-flex items-center justify-center gap-1.5">
            <Check className="h-4 w-4" /> Finish
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pose-session">
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <span key={s.pose} data-testid={`pose-session-step-${s.pose}`}
            className={`flex-1 h-1.5 ${done.includes(s.pose) ? "bg-[#34C759]" : i === idx ? "bg-[#E2E30A]" : "bg-white/15"}`} />
        ))}
        <button type="button" data-testid="pose-session-cancel" onClick={onDone}
          className="p-1 text-zinc-500 hover:text-white transition-colors" title="Leave the set">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">
          Step {idx + 1} of {STEPS.length} · {step.label}
        </p>
        <p className="text-[12px] text-zinc-300 mt-1">{step.hint}</p>
      </div>

      {busy ? (
        <div className="aspect-[3/4] grid place-items-center border border-white/10 bg-black">
          <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
        </div>
      ) : (
        <PoseCamera key={step.pose} pose={step.pose} ghostUrl={ghostFor(step.pose)}
          onCapture={capture} onCancel={onDone} />
      )}

      <button type="button" data-testid="pose-session-skip" onClick={advance} disabled={busy}
        className="w-full py-2.5 border border-white/10 text-zinc-400 hover:text-white text-[11px] uppercase tracking-wider transition-colors inline-flex items-center justify-center gap-1.5">
        <SkipForward className="h-3.5 w-3.5" /> Skip {step.pose}
      </button>
    </div>
  );
}
